import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Svg, { Circle, G } from 'react-native-svg';
import { SizableText, YStack } from 'tamagui';
import { SectionTitle, SubtitleText } from '@/components/ui/Typography';

interface UpNextOverlayProps {
  open: boolean;
  onClose: () => void;
  onPlay: () => void;
  nextVideoTitle: string;
  autoPlayDelay?: number;
}

export function UpNextOverlay({
  open,
  onClose,
  onPlay,
  nextVideoTitle,
  autoPlayDelay = 5,
}: UpNextOverlayProps) {
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasStartedRef = useRef(false);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      setIsReady(true);
    } else {
      setIsReady(false);
      setProgress(0);
    }
  }, [open]);

  useEffect(() => {
    if (!isReady || !open) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      hasStartedRef.current = false;
      return;
    }

    if (hasStartedRef.current) return;

    hasStartedRef.current = true;
    startTimeRef.current = Date.now();

    const duration = autoPlayDelay * 1000;
    const updateInterval = 50;

    intervalRef.current = setInterval(() => {
      if (!startTimeRef.current) return;
      
      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        hasStartedRef.current = false;
        onPlay();
      }
    }, updateInterval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isReady, open, autoPlayDelay]);

  if (!open || !isReady) return null;

  const size = 80;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const center = size / 2;

  return (
    <View style={styles.overlay}>
      <View style={styles.darkOverlay} />
      <BlurView intensity={20} style={StyleSheet.absoluteFill} />
      <YStack ai="center" jc="center" gap="$4" style={styles.content}>
        <SectionTitle color="white">
          Up Next
        </SectionTitle>
        <SubtitleText fontWeight="500" color="white" ta="center" px="$4">
          {nextVideoTitle}
        </SubtitleText>

        <TouchableOpacity
          onPress={onPlay} 
          style={styles.playButtonContainer}
          >
          <View style={[styles.circleContainer, { width: size, height: size }]}>
            <Svg width={size} height={size} style={styles.svg}>
              <Circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="rgba(255, 255, 255, 0.3)"
                strokeWidth={strokeWidth}
              />
              <G transform={`translate(${center}, ${center}) rotate(-90) translate(-${center}, -${center})`}>
                <Circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke="white"
                  strokeWidth={strokeWidth}
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              </G>
            </Svg>
            <View style={styles.iconContainer}>
              <Ionicons name="play" size={size * 0.4} color="white" />
            </View>
          </View>
        </TouchableOpacity>

        <TouchableOpacity onPress={onClose} style={styles.cancelButton}>
          <SizableText size="$3" color="white" textDecorationLine="underline">
            Cancel
          </SizableText>
        </TouchableOpacity>
      </YStack>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 0,
  },
  content: { zIndex: 1 },
  playButtonContainer: { justifyContent: 'center', alignItems: 'center' },
  circleContainer: { position: 'relative', justifyContent: 'center', alignItems: 'center' },
  svg: { position: 'absolute' },
  iconContainer: { position: 'absolute', justifyContent: 'center', alignItems: 'center' },
  cancelButton: { padding: 10 },
});